import type { Tool } from '@/features/traceability/type/Tool';
import { calculateBoxPlotStats, type BoxPlotData } from '../../utils/boxPlotUtils';

/**
 * Aggregate tool metrics into box plot data grouped by reference
 * @param tools - Array of tools with traces
 * @param selectedReferences - Array of selected reference names
 * @param metricExtractor - Function to extract metric value from a tool
 * @returns Array of box plot data per reference
 */
export function aggregateBoxPlotByReference(
  tools: Tool[],
  selectedReferences: string[],
  metricExtractor: (tool: Tool) => number
): BoxPlotData[] {
  return selectedReferences.map((reference) => {
    // Filter tools for this reference
    const refTools = tools.filter((tool) => tool.reference === reference);

    // Extract metric values for each tool
    const values = refTools.map(metricExtractor).filter((v) => !isNaN(v) && isFinite(v));

    // Calculate box plot statistics
    const stats = calculateBoxPlotStats(values);

    return {
      reference,
      ...stats,
    };
  });
}

/**
 * Extract total machining duration from a tool (sum of all traces)
 */
export function getTotalDuration(tool: Tool): number {
  if (!tool.traces || tool.traces.length === 0) return 0;
  return tool.traces.reduce((sum: number, trace) => sum + (trace.totalMachiningDuration ?? 0), 0);
}

/**
 * Extract total piece number from a tool (sum of all traces)
 */
export function getTotalPieceNumber(tool: Tool): number {
  if (!tool.traces || tool.traces.length === 0) return 0;
  return tool.traces.reduce((sum: number, trace) => sum + (trace.machinedPieces ?? 0), 0);
}

/**
 * Extract total volume removed from a tool (sum of all traces)
 */
export function getTotalVolumeRemoved(tool: Tool): number {
  if (!tool.traces || tool.traces.length === 0) return 0;
  return tool.traces.reduce((sum: number, trace) => sum + (trace.machinedVolume ?? 0), 0);
}

/**
 * Extract average malround from a tool (mean across traces)
 */
export function getAverageMalround(tool: Tool): number {
  if (!tool.traces || tool.traces.length === 0) return 0;

  const malroundValues = tool.traces
    .map((trace) => trace.malround)
    .filter((v: unknown): v is number => v != null && !isNaN(v as number) && isFinite(v as number));

  if (malroundValues.length === 0) return 0;

  return malroundValues.reduce((sum: number, v: number) => sum + v, 0) / malroundValues.length;
}
